import { CH_VIDEO, CH_DEVICE_MSG, CH_METADATA, TYPE_GET_CLIPBOARD, COPY_KEY_NONE, ACTION_DOWN, ACTION_UP, ACTION_MOVE, BUTTON_PRIMARY, DEV_MSG_CLIPBOARD, DEV_MSG_ACK_CLIPBOARD, PTS_CONFIG, PTS_KEYFRAME, PTS_MASK, CODEC_STRINGS, CODEC_MJPEG, buildKeycodeMsg, buildTextMsg, buildTouchMsg, buildScreenPowerMsg, } from '../core/protocol';
export class StreamSession {
    /** Latest known device clipboard value (cached from the stream WS). */
    get deviceClipboard() {
        return this.lastDeviceClipboard;
    }
    /**
     * Read the device clipboard on demand: send GET_CLIPBOARD and resolve with the
     * value scrcpy pushes back. Falls back to the cached value if the device doesn't
     * answer within timeoutMs (e.g. Android throttling). This is a single request per
     * call (a user gesture), not background polling.
     */
    requestClipboard(timeoutMs = 300) {
        if (this.readOnly || this.locked || this.ws?.readyState !== WebSocket.OPEN) {
            return Promise.resolve(this.lastDeviceClipboard);
        }
        return new Promise((resolve) => {
            let done = false;
            const finish = (text) => {
                if (done)
                    return;
                done = true;
                if (this.clipboardResolver === finish)
                    this.clipboardResolver = null;
                resolve(text);
            };
            this.clipboardResolver = finish;
            this.ws.send(new Uint8Array([TYPE_GET_CLIPBOARD, COPY_KEY_NONE]));
            setTimeout(() => finish(this.lastDeviceClipboard), timeoutMs);
        });
    }
    constructor(udid, canvas, ctx, onMeta, onClose, onOccupied, 
    // Read-only co-view: connect to ?action=watch, never attach input listeners,
    // and short-circuit every control sender. The decode pipeline is identical.
    readOnly = false, 
    /**
     * Guest (share-link) mode:
     *  - `shareToken` authorizes the socket in place of a panel session;
     *  - `pointerInput` drives the session from Pointer events instead of Mouse
     *    ones, so a finger on a phone works. PointerEvent extends MouseEvent, so
     *    the same handlers take both — the share page is usually opened on a phone.
     */
    guest = {}) {
        this.udid = udid;
        this.canvas = canvas;
        this.ctx = ctx;
        this.onMeta = onMeta;
        this.onClose = onClose;
        this.onOccupied = onOccupied;
        this.readOnly = readOnly;
        this.guest = guest;
        this.ws = null;
        this.decoder = null;
        this.configData = null;
        this.codecStr = 'avc1.42E01E';
        /**
         * The source sends whole JPEG stills instead of an H.264 elementary stream — an
         * aggregator phone, whose picture is a timed accessibility screen grab (no
         * MediaProjection, no scrcpy). Same framing on the wire, so only the decode differs:
         * every frame stands alone and there is no codec config to wait for.
         */
        this.mjpeg = false;
        /** Newest JPEG handed to the async decoder; older ones lose the race and are dropped. */
        this.jpegSeq = 0;
        this.destroyed = false;
        this.screenW = 1;
        this.screenH = 1;
        this.mouseDown = false;
        /**
         * Runtime read-only lock, independent of the constructor's `readOnly` co-view
         * flag: set true while the device's clicker is running so no human control leaks
         * to the farm. Unlike readOnly it can flip during the session's life (the SPA
         * calls setLocked on clicker start/stop). Every control sender honours it.
         */
        this.locked = false;
        this.reconnectTimer = null;
        this.deviceMsgBuf = new Uint8Array(0);
        this.lastDeviceClipboard = '';
        /**
         * Timestamp until which an inbound clipboard msg is treated as the connect-time
         * priming read (so it doesn't fire the "new copy" indicator). Only the first
         * message within this window is the priming reply; everything after — including
         * a genuine copy that lands while the priming reply is still in flight or lost —
         * counts as a real change. 0 = no priming pending.
         */
        this.clipboardPrimingUntil = 0;
        /** Fired when scrcpy pushes a genuinely new device-clipboard value (not the priming read). */
        this.onClipboardUpdate = null;
        /** Resolves the next inbound clipboard device-msg, used by requestClipboard(). */
        this.clipboardResolver = null;
        this.handleMessage = (ev) => {
            if (!(ev.data instanceof ArrayBuffer))
                return;
            const raw = new Uint8Array(ev.data);
            if (raw.length < 1)
                return;
            const channel = raw[0];
            const payload = raw.subarray(1);
            if (channel === CH_METADATA)
                this.handleMetadata(payload);
            else if (channel === CH_VIDEO)
                this.handleVideoFrame(payload);
            else if (channel === CH_DEVICE_MSG)
                this.handleDeviceMsg(payload);
        };
        this.onDocMouseMove = this.handleMouseMove.bind(this);
        this.onDocMouseUp = this.handleMouseUp.bind(this);
        if (!readOnly) {
            canvas.addEventListener(guest.pointerInput ? 'pointerdown' : 'mousedown', this.handleMouseDown.bind(this));
            canvas.addEventListener('contextmenu', (e) => e.preventDefault());
            canvas.style.cursor = 'pointer';
            // Without this the browser claims the gesture for scrolling/zooming and
            // the pointermove stream stops mid-drag.
            if (guest.pointerInput)
                canvas.style.touchAction = 'none';
        }
        this.connect();
    }
    /** Event pair the drag listeners use — pointer for touch mode, mouse otherwise. */
    get moveEvent() { return this.guest.pointerInput ? 'pointermove' : 'mousemove'; }
    get upEvent() { return this.guest.pointerInput ? 'pointerup' : 'mouseup'; }
    /** Toggle the runtime read-only lock (clicker running). No effect on a co-view
     * session, which is already read-only for its whole life. */
    setLocked(locked) {
        if (this.readOnly)
            return;
        this.locked = locked;
        this.canvas.style.cursor = locked ? 'not-allowed' : 'pointer';
    }
    connect() {
        if (this.destroyed)
            return;
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.decoder?.close();
        this.decoder = null;
        this.configData = null;
        // Re-learned from the next metadata frame; until then no frame is drawn either way.
        this.mjpeg = false;
        this.deviceMsgBuf = new Uint8Array(0);
        this.clipboardPrimingUntil = 0;
        this.mouseDown = false;
        document.removeEventListener(this.moveEvent, this.onDocMouseMove);
        document.removeEventListener(this.upEvent, this.onDocMouseUp);
        const proto = location.protocol === 'https:' ? 'wss' : 'ws';
        const action = this.readOnly ? 'watch' : 'stream';
        // A share token replaces the panel session for guest pages; the server resolves
        // it to exactly one udid and ignores the one in the query.
        const share = this.guest.shareToken ? `&share=${encodeURIComponent(this.guest.shareToken)}` : '';
        const ws = new WebSocket(`${proto}://${location.host}/?action=${action}&udid=${encodeURIComponent(this.udid)}&audio=false${share}`);
        ws.binaryType = 'arraybuffer';
        ws.onmessage = this.handleMessage;
        ws.onopen = () => { if (this.destroyed) {
            ws.close();
            return;
        } this.ws = ws; };
        ws.onclose = (event) => {
            if (this.ws === ws)
                this.ws = null;
            if (this.destroyed)
                return;
            if (event.code === 4001) {
                const by = event.reason.replace(/^taken:/, '');
                this.onOccupied?.(by);
                return;
            }
            this.onClose();
            // 4003 = kicked by admin, 4002 = superseded by our own newer session —
            // neither should auto-reconnect (4002 would ping-pong between tabs).
            if (event.code !== 4003 && event.code !== 4002) {
                this.reconnectTimer = setTimeout(() => this.connect(), 3000);
            }
        };
        ws.onerror = () => { if (!this.destroyed)
            this.onClose(); };
    }
    canvasPoint(clientX, clientY) {
        const r = this.canvas.getBoundingClientRect();
        // The canvas fills its wrap with `object-fit: contain`, so the actual video
        // is letterboxed inside the element rect (same aspect as screenW×screenH).
        // Map against that contained video rect, not the full element, or a
        // low-res / differently-shaped stream would offset every tap.
        const scale = Math.min(r.width / this.screenW, r.height / this.screenH);
        const dispW = this.screenW * scale;
        const dispH = this.screenH * scale;
        const offX = r.left + (r.width - dispW) / 2;
        const offY = r.top + (r.height - dispH) / 2;
        const clamp = (v, max) => Math.max(0, Math.min(max, v));
        return [
            Math.round(clamp((clientX - offX) / dispW * this.screenW, this.screenW)),
            Math.round(clamp((clientY - offY) / dispH * this.screenH, this.screenH)),
        ];
    }
    sendTouch(action, clientX, clientY, buttons) {
        if (this.readOnly || this.locked || this.ws?.readyState !== WebSocket.OPEN)
            return;
        const [px, py] = this.canvasPoint(clientX, clientY);
        this.ws.send(buildTouchMsg(action, px, py, this.screenW, this.screenH, buttons));
    }
    sendKeycode(keycode, metaState = 0) {
        if (this.readOnly || this.locked || this.ws?.readyState !== WebSocket.OPEN)
            return;
        this.ws.send(buildKeycodeMsg(ACTION_DOWN, keycode, metaState));
        this.ws.send(buildKeycodeMsg(ACTION_UP, keycode, metaState));
    }
    /** Inject typed text on the device via scrcpy INJECT_TEXT. */
    sendText(text) {
        if (this.readOnly || this.locked || !text || this.ws?.readyState !== WebSocket.OPEN)
            return;
        this.ws.send(buildTextMsg(text));
    }
    /**
     * Turn the device's physical screen off (on=false) or back on (on=true) while
     * keeping this stream live (scrcpy SET_SCREEN_POWER_MODE). Returns false when
     * there's no open stream to carry the control message.
     */
    sendScreenPower(on) {
        if (this.readOnly || this.locked || this.ws?.readyState !== WebSocket.OPEN)
            return false;
        this.ws.send(buildScreenPowerMsg(on));
        // Keep an aggregator phone's screen wake lock in sync with the display, so a
        // deliberate screen-off isn't fought by the keep-awake lock (host farms ignore
        // it). Best-effort/fire-and-forget — the scrcpy control above is what matters.
        void fetch(`/api/devices/${encodeURIComponent(this.udid)}/keep-awake`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ on }),
        }).catch(() => { });
        return true;
    }
    handleMouseDown(e) {
        if (e.button !== 0)
            return;
        e.preventDefault();
        this.mouseDown = true;
        this.sendTouch(ACTION_DOWN, e.clientX, e.clientY, BUTTON_PRIMARY);
        document.addEventListener(this.moveEvent, this.onDocMouseMove);
        document.addEventListener(this.upEvent, this.onDocMouseUp);
    }
    handleMouseMove(e) {
        if (!this.mouseDown)
            return;
        this.sendTouch(ACTION_MOVE, e.clientX, e.clientY, BUTTON_PRIMARY);
    }
    handleMouseUp(e) {
        if (e.button !== 0)
            return;
        this.mouseDown = false;
        this.sendTouch(ACTION_UP, e.clientX, e.clientY, 0);
        document.removeEventListener(this.moveEvent, this.onDocMouseMove);
        document.removeEventListener(this.upEvent, this.onDocMouseUp);
    }
    // scrcpy device messages (clipboard / ack) arrive framed on CH_DEVICE_MSG.
    // TCP can split or coalesce them, so reassemble across frames before parsing.
    handleDeviceMsg(payload) {
        if (this.deviceMsgBuf.length) {
            const merged = new Uint8Array(this.deviceMsgBuf.length + payload.length);
            merged.set(this.deviceMsgBuf);
            merged.set(payload, this.deviceMsgBuf.length);
            this.deviceMsgBuf = merged;
        }
        else {
            this.deviceMsgBuf = payload.slice();
        }
        const buf = this.deviceMsgBuf;
        const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
        let off = 0;
        while (off < buf.length) {
            const type = buf[off];
            if (type === DEV_MSG_CLIPBOARD) {
                if (off + 5 > buf.length)
                    break; // need type(1) + length(4)
                const len = view.getUint32(off + 1, false);
                if (off + 5 + len > buf.length)
                    break; // text not fully arrived yet
                const text = new TextDecoder().decode(buf.subarray(off + 5, off + 5 + len));
                off += 5 + len;
                // Clipboard arrives either as an unsolicited push (scrcpy autosync on
                // a real device copy) or as the reply to our GET_CLIPBOARD. Cache the
                // value; the Copy button reads it on click (a user gesture, which Chrome
                // requires for clipboard writes).
                const prev = this.lastDeviceClipboard;
                // Only the first message inside the priming window is the connect-time
                // baseline; consume the window so later copies always register.
                const isPriming = this.clipboardPrimingUntil > 0 && Date.now() < this.clipboardPrimingUntil;
                this.clipboardPrimingUntil = 0;
                this.lastDeviceClipboard = text;
                // Resolve a pending requestClipboard() with the fresh value.
                this.clipboardResolver?.(text);
                // Light up the Copy button for genuine new copies — skip only the
                // priming read on connect and echoes of the same value.
                if (!isPriming && text && text !== prev) {
                    this.onClipboardUpdate?.(text);
                }
            }
            else if (type === DEV_MSG_ACK_CLIPBOARD) {
                if (off + 9 > buf.length)
                    break; // type(1) + sequence(8)
                off += 9;
            }
            else {
                off = buf.length; // unknown type: cannot frame, resync
            }
        }
        this.deviceMsgBuf = off >= buf.length ? new Uint8Array(0) : buf.subarray(off).slice();
    }
    handleMetadata(payload) {
        try {
            const meta = JSON.parse(new TextDecoder().decode(payload));
            this.canvas.width = meta.screenWidth;
            this.canvas.height = meta.screenHeight;
            this.screenW = meta.screenWidth;
            this.screenH = meta.screenHeight;
            this.mjpeg = meta.videoCodec === CODEC_MJPEG;
            this.codecStr = CODEC_STRINGS[meta.videoCodec] ?? 'avc1.42E01E';
            this.onMeta(meta);
            // Prime the clipboard cache so the Copy button has a value even before
            // the device's clipboard changes (autosync only pushes on change). Mark a
            // short window so the priming reply doesn't fire the "new copy" indicator,
            // but a real copy landing right after connect still does. Skipped for a
            // still-frame source: there is no scrcpy on the far end to answer it, so the
            // request would only be logged there as unsupported control.
            if (!this.mjpeg && !this.readOnly && !this.locked && this.ws?.readyState === WebSocket.OPEN) {
                this.clipboardPrimingUntil = Date.now() + 1500;
                this.ws.send(new Uint8Array([TYPE_GET_CLIPBOARD, COPY_KEY_NONE]));
            }
        }
        catch { /* ignore */ }
    }
    handleVideoFrame(payload) {
        if (payload.length < 12)
            return;
        const view = new DataView(payload.buffer, payload.byteOffset);
        const rawPts = view.getBigUint64(0);
        const size = view.getUint32(8);
        const data = payload.subarray(12, 12 + size);
        const isConfig = (rawPts & PTS_CONFIG) !== 0n;
        const isKeyframe = (rawPts & PTS_KEYFRAME) !== 0n;
        const pts = rawPts & PTS_MASK;
        if (this.mjpeg) {
            this.drawStill(data);
            return;
        }
        if (isConfig) {
            this.configData = data.slice();
            this.resetDecoder();
            return;
        }
        if (!this.decoder || this.decoder.state !== 'configured')
            return;
        let frameData = data;
        if (isKeyframe && this.configData) {
            const merged = new Uint8Array(this.configData.length + data.length);
            merged.set(this.configData);
            merged.set(data, this.configData.length);
            frameData = merged;
        }
        try {
            this.decoder.decode(new EncodedVideoChunk({ type: isKeyframe ? 'key' : 'delta', timestamp: Number(pts), data: frameData }));
        }
        catch {
            this.resetDecoder();
        }
    }
    /**
     * Paint one JPEG still (aggregator phone). Decoding is async, so a frame that
     * finishes after a newer one has arrived is thrown away rather than drawn over the
     * fresher picture. The canvas follows the frame's own size: the phone re-scales off
     * each grab, so a rotation changes the aspect mid-session and the metadata's
     * dimensions stop being the truth.
     */
    drawStill(data) {
        const seq = ++this.jpegSeq;
        void createImageBitmap(new Blob([data.slice()], { type: 'image/jpeg' }))
            .then((bitmap) => {
            if (this.destroyed || seq !== this.jpegSeq) {
                bitmap.close();
                return;
            }
            if (this.canvas.width !== bitmap.width || this.canvas.height !== bitmap.height) {
                this.canvas.width = bitmap.width;
                this.canvas.height = bitmap.height;
            }
            this.ctx.drawImage(bitmap, 0, 0, this.canvas.width, this.canvas.height);
            bitmap.close();
        })
            .catch(() => { });
    }
    resetDecoder() {
        this.decoder?.close();
        this.decoder = new VideoDecoder({
            output: (frame) => { this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height); frame.close(); },
            error: () => { this.decoder = null; },
        });
        this.decoder.configure({ codec: this.codecStr, codedWidth: this.canvas.width || 1, codedHeight: this.canvas.height || 1 });
    }
    close() {
        this.destroyed = true;
        this.mouseDown = false;
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        document.removeEventListener(this.moveEvent, this.onDocMouseMove);
        document.removeEventListener(this.upEvent, this.onDocMouseUp);
        this.decoder?.close();
        this.decoder = null;
        if (this.ws && this.ws.readyState < WebSocket.CLOSING)
            this.ws.close();
        this.ws = null;
    }
}
